import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth, apiClient } from '../App';
import { Truck, Mail, Lock, User, Phone, Building, ArrowLeft, Eye, EyeOff, Shield, AlertTriangle, FileText, Calendar, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, user } = useAuth();
  
  const [mode, setMode] = useState('login'); // login, register, verify, coi
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingUserId, setPendingUserId] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [pendingUserData, setPendingUserData] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: searchParams.get('role') || 'customer',
    company_name: '',
    dot_number: '',
    mc_number: '',
  });
  
  const [coiData, setCoiData] = useState({
    insurance_company: '',
    policy_number: '',
    expiration_date: '',
    liability_coverage: '',
    cargo_coverage: '',
  });
  
  const [otp, setOtp] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToFMCSA, setAgreedToFMCSA] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // All users must agree to Terms of Service
    if (!agreedToTerms) {
      toast.error('You must agree to the Terms of Service to register');
      return;
    }
    
    // Require FMCSA agreement for carriers and drivers
    if ((formData.role === 'carrier' || formData.role === 'driver') && !agreedToFMCSA) {
      toast.error('You must agree to the FMCSA Compliance Agreement to register');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await apiClient.post('/auth/register', formData);
      if (response.data.requires_2fa) {
        setPendingEmail(formData.email);
        setMode('verify');
        toast.success('Account created! Please verify with the OTP sent to your phone.');
        // In development, OTP is logged in backend
        toast.info('Development: Check backend logs for OTP');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await apiClient.post('/auth/login', {
        email: formData.email,
        password: formData.password,
      });
      
      if (response.data.requires_2fa) {
        setPendingEmail(formData.email);
        setMode('verify');
        toast.success('Please verify with the OTP sent to your phone.');
        toast.info('Development: Check backend logs for OTP');
      } else {
        login(response.data.access_token, response.data.user);
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Please enter complete OTP');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await apiClient.post('/auth/verify-otp', {
        email: pendingEmail,
        otp: otp,
      });
      
      // Check if carrier needs to submit COI
      if (response.data.user.role === 'carrier' && !response.data.user.has_valid_coi) {
        setPendingToken(response.data.access_token);
        setPendingUserData(response.data.user);
        setMode('coi');
        toast.success('Verification successful! Please submit your Certificate of Insurance.');
      } else {
        login(response.data.access_token, response.data.user);
        toast.success('Verification successful!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCOI = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Set auth header temporarily
      const originalAuth = apiClient.defaults.headers.common['Authorization'];
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${pendingToken}`;
      
      await apiClient.post('/coi', {
        insurance_company: coiData.insurance_company,
        policy_number: coiData.policy_number,
        expiration_date: coiData.expiration_date,
        liability_coverage: parseFloat(coiData.liability_coverage),
        cargo_coverage: coiData.cargo_coverage ? parseFloat(coiData.cargo_coverage) : null,
      });
      
      // Restore or keep auth
      login(pendingToken, { ...pendingUserData, has_valid_coi: true });
      toast.success('COI submitted successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit COI');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      await apiClient.post('/auth/resend-otp', null, { params: { email: pendingEmail } });
      toast.success('OTP resent! Check backend logs in development.');
    } catch (error) {
      toast.error('Failed to resend OTP');
    }
  };

  if (mode === 'verify') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-8">
              <Truck className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-bold text-slate-900">True Choice Enterprise</span>
            </Link>
            <h1 className="text-2xl font-bold text-slate-900">Verify Your Phone</h1>
            <p className="text-slate-600 mt-2">Enter the 6-digit code sent to your phone</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              
              <Button 
                type="submit" 
                className="btn-accent w-full" 
                disabled={loading || otp.length !== 6}
                data-testid="verify-otp-btn"
              >
                {loading ? 'Verifying...' : 'Verify & Continue'}
              </Button>
              
              <div className="text-center">
                <button 
                  type="button" 
                  onClick={handleResendOTP}
                  className="text-orange-500 hover:text-orange-600 text-sm"
                  data-testid="resend-otp-btn"
                >
                  Didn&apos;t receive code? Resend
                </button>
              </div>
            </form>
            
            <button 
              onClick={() => setMode('login')}
              className="flex items-center justify-center gap-2 w-full mt-6 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4" /> Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // COI Submission Form for Carriers
  if (mode === 'coi') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-8">
              <Truck className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-bold text-slate-900">True Choice Enterprise</span>
            </Link>
            <h1 className="text-2xl font-bold text-slate-900">Certificate of Insurance</h1>
            <p className="text-slate-600 mt-2">Submit your master COI to complete registration</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {/* FMCSA Requirements Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-blue-500 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-900">FMCSA Compliance Required</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Minimum liability coverage: <strong>$750,000</strong> for general freight
                  </p>
                  <p className="text-sm text-blue-700">
                    Hazmat carriers require up to <strong>$5,000,000</strong> coverage
                  </p>
                </div>
              </div>
            </div>
            
            <form onSubmit={handleSubmitCOI} className="space-y-4">
              <div>
                <Label htmlFor="insurance_company">Insurance Company *</Label>
                <div className="relative mt-1">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="insurance_company"
                    value={coiData.insurance_company}
                    onChange={(e) => setCoiData({ ...coiData, insurance_company: e.target.value })}
                    className="pl-10"
                    placeholder="e.g., Progressive Commercial"
                    required
                    data-testid="coi-insurance-company"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="policy_number">Policy Number *</Label>
                <div className="relative mt-1">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="policy_number"
                    value={coiData.policy_number}
                    onChange={(e) => setCoiData({ ...coiData, policy_number: e.target.value })}
                    className="pl-10"
                    placeholder="e.g., POL-12345678"
                    required
                    data-testid="coi-policy-number"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="expiration_date">Expiration Date *</Label>
                <div className="relative mt-1">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="expiration_date"
                    type="date"
                    value={coiData.expiration_date}
                    onChange={(e) => setCoiData({ ...coiData, expiration_date: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="coi-expiration-date"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="liability_coverage">Liability Coverage ($) *</Label>
                  <div className="relative mt-1">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="liability_coverage"
                      type="number"
                      min="750000"
                      step="1000"
                      value={coiData.liability_coverage}
                      onChange={(e) => setCoiData({ ...coiData, liability_coverage: e.target.value })}
                      className="pl-10"
                      placeholder="750000"
                      required
                      data-testid="coi-liability-coverage"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Min: $750,000</p>
                </div>
                
                <div>
                  <Label htmlFor="cargo_coverage">Cargo Coverage ($)</Label>
                  <div className="relative mt-1">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="cargo_coverage"
                      type="number"
                      min="0"
                      step="1000"
                      value={coiData.cargo_coverage}
                      onChange={(e) => setCoiData({ ...coiData, cargo_coverage: e.target.value })}
                      className="pl-10"
                      placeholder="100000"
                      data-testid="coi-cargo-coverage"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Optional</p>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 inline-block mr-1" />
                  Individual customers may request a COI naming their company as additional insured. 
                  You&apos;ll receive email notifications for such requests.
                </p>
              </div>
              
              <Button 
                type="submit" 
                className="btn-accent w-full mt-6" 
                disabled={loading || !coiData.insurance_company || !coiData.policy_number || !coiData.expiration_date || !coiData.liability_coverage}
                data-testid="submit-coi-btn"
              >
                {loading ? 'Submitting...' : 'Submit COI & Complete Registration'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-8">
              <Truck className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-bold text-slate-900">True Choice Enterprise</span>
            </Link>
            <h1 className="text-2xl font-bold text-slate-900">
              {mode === 'login' ? 'Welcome Back' : 'Create Your Account'}
            </h1>
            <p className="text-slate-600 mt-2">
              {mode === 'login' ? 'Sign in to your account' : 'Your one-stop shop for moving anything'}
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {mode === 'register' && (
              <div className="mb-6">
                <Label>I am a...</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger className="mt-1" data-testid="role-select">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Customer (Individual or Company)</SelectItem>
                    <SelectItem value="carrier">Carrier Company</SelectItem>
                    <SelectItem value="driver">Driver (Looking for work)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <form onSubmit={mode === 'login' ? handleLogin : handleRegister} className="space-y-4">
              {mode === 'register' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name">First Name</Label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="first_name"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleInputChange}
                          className="pl-10"
                          required
                          data-testid="first-name-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="last_name">Last Name</Label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="last_name"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleInputChange}
                          className="pl-10"
                          required
                          data-testid="last-name-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="pl-10"
                        placeholder="+1234567890"
                        required
                        data-testid="phone-input"
                      />
                    </div>
                  </div>
                  
                  {formData.role === 'carrier' && (
                    <>
                      <div>
                        <Label htmlFor="company_name">Company Name</Label>
                        <div className="relative mt-1">
                          <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            id="company_name"
                            name="company_name"
                            value={formData.company_name}
                            onChange={handleInputChange}
                            className="pl-10"
                            data-testid="company-name-input"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="dot_number">DOT Number</Label>
                          <Input
                            id="dot_number"
                            name="dot_number"
                            value={formData.dot_number}
                            onChange={handleInputChange}
                            className="mt-1"
                            data-testid="dot-number-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="mc_number">MC Number</Label>
                          <Input
                            id="mc_number"
                            name="mc_number"
                            value={formData.mc_number}
                            onChange={handleInputChange}
                            className="mt-1"
                            data-testid="mc-number-input"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
              
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    className="pl-10 pr-10"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              {/* Terms of Service Agreement - Required for ALL users */}
              {mode === 'register' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="terms-agreement"
                      checked={agreedToTerms}
                      onCheckedChange={setAgreedToTerms}
                      className="mt-1"
                      data-testid="terms-agreement-checkbox"
                    />
                    <div className="flex-1">
                      <label htmlFor="terms-agreement" className="text-sm text-slate-700 cursor-pointer">
                        <AlertTriangle className="h-4 w-4 inline-block text-red-500 mr-1" />
                        I have read and agree to the{' '}
                        <Link 
                          to="/terms-of-service" 
                          target="_blank"
                          className="text-orange-500 font-semibold hover:text-orange-600 underline"
                        >
                          Terms of Service
                        </Link>
                        . I understand that True Choice Enterprise is a <strong>BROKER-FREE PLATFORM</strong> and use is <strong>AT MY OWN RISK</strong>. True Choice Enterprise is not responsible for any transactions, does not recommend any persons or businesses, and does not assume responsibility for any financial or property loss or gain.
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* FMCSA Agreement for Carriers and Drivers */}
              {mode === 'register' && (formData.role === 'carrier' || formData.role === 'driver') && (
                <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="fmcsa-agreement"
                      checked={agreedToFMCSA}
                      onCheckedChange={setAgreedToFMCSA}
                      className="mt-1"
                      data-testid="fmcsa-agreement-checkbox"
                    />
                    <div className="flex-1">
                      <label htmlFor="fmcsa-agreement" className="text-sm text-slate-700 cursor-pointer">
                        <Shield className="h-4 w-4 inline-block text-orange-500 mr-1" />
                        I have read and agree to the{' '}
                        <Link 
                          to="/user-agreement" 
                          target="_blank"
                          className="text-orange-500 font-semibold hover:text-orange-600 underline"
                        >
                          FMCSA Compliance Agreement
                        </Link>
                        . I certify that I will abide by all Federal Motor Carrier Safety Administration regulations at all times.
                      </label>
                    </div>
                  </div>
                </div>
              )}
              
              <Button 
                type="submit" 
                className="btn-accent w-full mt-6" 
                disabled={loading || (mode === 'register' && !agreedToTerms) || (mode === 'register' && (formData.role === 'carrier' || formData.role === 'driver') && !agreedToFMCSA)}
                data-testid={mode === 'login' ? 'login-btn' : 'register-btn'}
              >
                {loading ? 'Please wait...' : (mode === 'login' ? 'Sign In' : 'Create Account')}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              {mode === 'login' ? (
                <p className="text-slate-600">
                  Don&apos;t have an account?{' '}
                  <button 
                    onClick={() => setMode('register')} 
                    className="text-orange-500 font-semibold hover:text-orange-600"
                    data-testid="switch-to-register"
                  >
                    Sign Up
                  </button>
                </p>
              ) : (
                <p className="text-slate-600">
                  Already have an account?{' '}
                  <button 
                    onClick={() => setMode('login')} 
                    className="text-orange-500 font-semibold hover:text-orange-600"
                    data-testid="switch-to-login"
                  >
                    Sign In
                  </button>
                </p>
              )}
            </div>
          </div>
          
          <Link to="/" className="flex items-center justify-center gap-2 mt-6 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
        </div>
      </div>
      
      {/* Right side - Image (hidden on mobile) */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1714647212106-c649ee06a117?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Moving made easy"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <h2 className="text-4xl font-bold mb-4">Skip the Broker</h2>
            <p className="text-xl text-slate-200">
              Connect directly with verified trucking carriers. Save time and money on your next move.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
